import React, { useEffect, useState } from "react"
import { Col, Row, Card, Spinner, Container } from "reactstrap"
import Chart from "react-apexcharts"
import { fetchFinanceDashGraph } from "../../services/finance/finance"
import { createSelector } from "reselect"
import { useSelector } from "react-redux"
import moment from "moment"
import NumberFormat from "../../utils/numberFormat"
import { DashStyles, PALETTE } from "../Dashboard/dashkit"
import useCollapsedSidebar from "../../hooks/useCollapsedSidebar"

const P = PALETTE
const MONTHS = ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar", "Apr", "May", "Jun"]

const FinanceDahboard = ({ embedded = false }) => {
  useCollapsedSidebar()
  const companyname = localStorage.getItem("companyname")
  document.title = `Finance Dashboard${companyname ? ` | ${companyname}` : ""}`

  const [purchaseData, setPurchaseData] = useState([])
  const [salesData, setSalesData] = useState([])
  const [graphMonths, setGraphMonths] = useState([])
  const [cardData, setCardData] = useState()
  // The "others" aggregate is far heavier than the two graph calls, so it loads on its
  // own track with this spinner while the charts render immediately.
  const [cardLoading, setCardLoading] = useState(true)
  // Editable reporting window (YYYY-MM-DD for the native date inputs). Seeded from the
  // logged-in user's financial year, but the user can override either end.
  const [dateRange, setDateRange] = useState({ from: "", to: "" })

  const selectLoginState = state => state.Login
  const LoginProperties = createSelector(selectLoginState, login => ({
    loggedUser: login.loggedUser,
  }))

  const { loggedUser } = useSelector(LoginProperties)
  const desimalrange = localStorage.getItem("desimalrange")

  // Seed the reporting window from the financial year once, without clobbering a range
  // the user has already picked.
  useEffect(() => {
    if (!loggedUser?.finance_year) return
    setDateRange(prev =>
      prev.from && prev.to
        ? prev
        : {
            from: moment(loggedUser.finance_year.start_date).format("YYYY-MM-DD"),
            to: moment(loggedUser.finance_year.end_date).format("YYYY-MM-DD"),
          }
    )
  }, [loggedUser])

  useEffect(() => {
    if (!dateRange.from || !dateRange.to) return

    const from = moment(dateRange.from).format("DD-MMM-YYYY")
    const to = moment(dateRange.to).format("DD-MMM-YYYY")

    // Charts: the two graph calls are quick — run them together and paint the charts as
    // soon as they return instead of blocking on the heavy "others" aggregate.
    Promise.all([
      fetchFinanceDashGraph({ params: `?from=${from}&to=${to}&type=purchase` }),
      fetchFinanceDashGraph({ params: `?from=${from}&to=${to}&type=sales` }),
    ])
      .then(([purchaseResponce, salesResponce]) => {
        setGraphMonths(
          Object.keys(purchaseResponce.data.graph).map(month =>
            moment(month, "MMM YYYY").format("MMM")
          )
        )
        setPurchaseData(
          Object.values(purchaseResponce.data.graph).map(Number)
        )
        setSalesData(Object.values(salesResponce.data.graph).map(Number))
      })
      .catch(() => {})

    // Cards (gross/net profit, assets, balances, …): heavier, so load on its own track.
    setCardLoading(true)
    fetchFinanceDashGraph({ params: `?from=${from}&to=${to}&type=others` })
      .then(res => setCardData(res))
      .catch(() => {})
      .finally(() => setCardLoading(false))
  }, [dateRange])

  var Options = {
    Series: [{ name: "Sales", type: "area", data: salesData }],
    chart: { type: "area", toolbar: { show: false } },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    colors: [P.indigo],
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] } },
    xaxis: { categories: graphMonths },
    yaxis: {
      labels: {
        formatter: function (value) {
          if (value >= 1000 && value < 1000000) {
            return (value / 1000).toFixed(desimalrange) + "k"
          } else if (value >= 1000000) {
            return (value / 1000000).toFixed(desimalrange) + "l"
          } else {
            return value
          }
        },
      },
    },
    tooltip: { y: { formatter: function (val) { return val } } },
    grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
  }

  var Options1 = {
    Series: [{ name: "Purchase", type: "area", data: purchaseData }],
    chart: { type: "area", toolbar: { show: false } },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    colors: [P.teal],
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] } },
    xaxis: { categories: graphMonths },
    yaxis: {
      labels: {
        formatter: function (value) {
          if (value >= 1000 && value < 1000000) {
            return (value / 1000).toFixed(desimalrange) + "k"
          } else if (value >= 1000000) {
            return (value / 1000000).toFixed(desimalrange) + "l"
          } else {
            return value
          }
        },
      },
    },
    tooltip: { y: { formatter: function (val) { return val } } },
    grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
  }

  // Income statement & monthly sales — static exec figures (placeholders, wire per-scope later)
  const incomeStmt = {
    options: {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { columnWidth: "55%", borderRadius: 4, distributed: true } },
      colors: [P.indigo, P.red, P.cyan, P.red, P.cyan, P.red, P.green],
      dataLabels: { enabled: false },
      legend: { show: false },
      xaxis: {
        categories: ["Total Revenue", "Cost of Goods Sold", "Gross Profit", "Operating Expense", "Operating Income", "Other Expense / Tax", "Net Profit"],
        labels: { rotate: -35, style: { colors: "#94a3b8", fontSize: "10px" } },
        axisTicks: { show: false },
      },
      yaxis: { labels: { formatter: v => `${Math.round(v)}M`, style: { colors: "#94a3b8" } } },
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
      tooltip: { y: { formatter: v => `${Math.round(v)}M` } },
    },
    series: [{ name: "Amount", data: [108, 60, 48, 30, 18, 4, 14] }],
  }
  const monthlySales = {
    options: {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
      colors: [P.indigo],
      dataLabels: { enabled: false },
      legend: { show: false },
      xaxis: { categories: ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar", "Apr", "May", "Jun"], labels: { style: { colors: "#94a3b8", fontSize: "10px" } } },
      yaxis: { labels: { formatter: v => `${v}M`, style: { colors: "#94a3b8" } } },
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
      tooltip: { y: { formatter: v => `${v}M` } },
    },
    series: [{ name: "Sales", data: [7.6, 8.1, 8.4, 9.0, 8.7, 9.3, 8.9, 9.6, 10.1, 9.8, 10.4, 11.0] }],
  }

  // Employee cost vs sales combo + working-capital day gauges (static exec figures)
  const gauge = color => ({
    chart: { type: "radialBar", sparkline: { enabled: true }, fontFamily: "inherit" },
    plotOptions: { radialBar: { hollow: { size: "58%" }, track: { background: "#eef0f4" }, dataLabels: { name: { show: false }, value: { fontSize: "20px", fontWeight: 700, color: "#0f172a", offsetY: 6, formatter: v => `${Math.round(v)}` } } } },
    colors: [color], stroke: { lineCap: "round" },
  })
  const empCostVsSales = {
    options: {
      chart: { type: "line", toolbar: { show: false }, fontFamily: "inherit" },
      stroke: { width: [0, 3], curve: "smooth" }, colors: [P.indigo, P.amber],
      plotOptions: { bar: { columnWidth: "45%", borderRadius: 3 } },
      dataLabels: { enabled: false }, legend: { show: false },
      xaxis: { categories: MONTHS, labels: { style: { colors: "#94a3b8", fontSize: "10px" } } },
      yaxis: [{ labels: { formatter: v => `${v}M`, style: { colors: "#94a3b8" } } }, { opposite: true, labels: { formatter: v => `${v}%`, style: { colors: "#94a3b8" } } }],
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
    },
    series: [
      { name: "Sales", type: "column", data: [7.6, 8.1, 8.4, 9, 8.7, 9.3, 8.9, 9.6, 10.1, 9.8, 10.4, 11] },
      { name: "Emp cost %", type: "line", data: [22, 21, 20, 19, 20, 18, 19, 18, 17, 18, 16, 15] },
    ],
  }

  // ---- helpers for the chart cards (exact details from the API + a small chart) ----
  const absN = v => Math.abs(Number(v || 0))
  const fmt = v => NumberFormat(absN(v), desimalrange, desimalrange)
  const crdr = v => (Number(v) >= 0 ? "Cr" : "Dr")
  // compact number for chart axes/tooltips (8.78M / 12.30K)
  const fmtShort = v => {
    const n = Math.abs(Number(v || 0))
    if (!isFinite(n)) return "0"
    if (n >= 1e6) return (n / 1e6).toFixed(1) + "M"
    if (n >= 1e3) return (n / 1e3).toFixed(1) + "K"
    return `${Math.round(n)}`
  }
  // balance ratio in "1 : x" / "x : 1" form (matches the old Assets/Liabilities logic)
  const balRatio = (a, b) => {
    const x = Math.abs(Number(a)), y = Math.abs(Number(b))
    if (!x || !y || !isFinite(x / y)) return `-`
    return x >= y ? `1 : ${(x / y).toFixed(desimalrange)}` : `${(x / y).toFixed(desimalrange)} : 1`
  }
  // percentage ratio (profit ÷ sales); "-" when no data
  const pctRatio = (a, b) => {
    const n = Number(a), d = Number(b)
    if (!d || !isFinite(n / d)) return `-`
    return `${Math.abs((n / d) * 100).toFixed(desimalrange)} %`
  }
  // equity proxy = current assets − current liabilities (net worth); API has no equity field
  const equityVal =
    Number(cardData?.current_assets || 0) - Number(cardData?.current_liabilities || 0)

  // small bar / donut chart option builders
  const barOpts = (categories, colors) => ({
    chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
    plotOptions: { bar: { columnWidth: "50%", borderRadius: 3, distributed: true } },
    colors,
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: { categories, labels: { style: { fontSize: "9.5px", colors: "#94a3b8" }, trim: true } },
    yaxis: { labels: { formatter: fmtShort, style: { colors: "#94a3b8", fontSize: "10px" } } },
    grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
    tooltip: { y: { formatter: fmtShort } },
  })
  const donutOpts = (labels, colors) => ({
    chart: { type: "donut", fontFamily: "inherit" },
    labels,
    colors,
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    plotOptions: { pie: { donut: { size: "68%" } } },
    tooltip: { y: { formatter: fmtShort } },
  })

  // value list rows (exact figures), with a colour dot matching the chart series
  const valLi = (label, val, dot) => (
    <li key={label}>
      <span className="dk-dot" style={{ background: dot }} />
      <span className="nm">{label}</span>
      <span className="vl">{fmt(val)} {crdr(val)}</span>
    </li>
  )
  // P&L rows: on a profit line a debit (negative) balance is actually a LOSS,
  // so flip the wording (Profit → Loss) and paint it red instead of green.
  const plLi = (base, val, dot) => {
    const loss = Number(val) < 0
    const label = loss ? base.replace(/Profit/i, "Loss") : base
    return valLi(label, val, loss ? P.red : dot)
  }
  const txtLi = (label, text) => (
    <li key={label}>
      <span className="nm" style={{ marginLeft: 17 }}>{label}</span>
      <span className="vl">{text}</span>
    </li>
  )
  const chartCard = (title, chart, rows) => (
    <Col md={6} xl={3} key={title}>
      <div className="dk-card h-100">
        <div className="finance-dash-head mb-2">{title}</div>
        {chart}
        <ul className="dk-list mb-0">{rows}</ul>
      </div>
    </Col>
  )

  return (
    <div className={embedded ? "fin-dash-embedded" : "page-content"}>
      <DashStyles />
      <Container fluid>
        <style>{`
          .fin-head{display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap;margin-bottom:18px;}
          .fin-title{font-weight:700;color:#0f172a;margin:0;font-size:22px;}
          .fin-daterange{display:flex;align-items:center;gap:8px;background:#fff;border:1px solid #e5e7eb;border-radius:10px;padding:6px 12px;font-size:13px;color:#334155;font-weight:600;}
          .fin-daterange input{border:none;outline:none;background:transparent;font-size:13px;color:#334155;font-weight:600;font-family:inherit;cursor:pointer;padding:2px 2px;}
          .fin-daterange input::-webkit-calendar-picker-indicator{cursor:pointer;opacity:.55;}
          .fin-daterange .sep{color:#94a3b8;}
        `}</style>
        <div className="fin-head">
          <h4 className="fin-title">Finance Overview</h4>
          <div className="fin-daterange">
            <i className="bx bx-calendar" />
            <input
              type="date"
              value={dateRange.from}
              max={dateRange.to || undefined}
              onChange={e => setDateRange(r => ({ ...r, from: e.target.value }))}
            />
            <span className="sep">–</span>
            <input
              type="date"
              value={dateRange.to}
              min={dateRange.from || undefined}
              onChange={e => setDateRange(r => ({ ...r, to: e.target.value }))}
            />
          </div>
        </div>
      {/* Metric cards (first section) */}
      <div className="mb-3" style={{ opacity: cardLoading ? 0.5 : 1, transition: "opacity .25s" }}>
        {cardLoading && (
          <div className="d-flex align-items-center text-muted px-3 pb-1" style={{ fontSize: 12, gap: 8 }}>
            <Spinner size="sm" /> Calculating financials…
          </div>
        )}
        <Row className="g-3">
          {/* Donut cards (shorter row) */}
          {chartCard(
            "Current Ratio",
            <Chart type="donut" height={110}
              options={donutOpts(["Current Assets", "Current Liabilities"], [P.indigo, P.amber])}
              series={[absN(cardData?.current_assets), absN(cardData?.current_liabilities)]} />,
            [
              valLi("Current Assets", cardData?.current_assets, P.indigo),
              valLi("Current Liabilities", cardData?.current_liabilities, P.amber),
              txtLi("Ratio", balRatio(cardData?.current_assets, cardData?.current_liabilities)),
            ]
          )}

          {chartCard(
            "Receivable/Payable",
            <Chart type="donut" height={110}
              options={donutOpts(["Receivable", "Payable"], [P.green, P.red])}
              series={[absN(cardData?.total_sales), absN(cardData?.total_purchase)]} />,
            [
              valLi("Receivable", cardData?.total_sales, P.green),
              valLi("Payable", cardData?.total_purchase, P.red),
              txtLi("Ratio", balRatio(cardData?.total_sales, cardData?.total_purchase)),
            ]
          )}

          {chartCard(
            "Cash/Bank Accounts",
            <Chart type="donut" height={110}
              options={donutOpts(["Cash in Hand", "Bank Accounts"], [P.cyan, P.violet])}
              series={[absN(cardData?.cash_in_hand), absN(cardData?.bank_balance)]} />,
            [
              valLi("Cash in Hand", cardData?.cash_in_hand, P.cyan),
              valLi("Bank Accounts", cardData?.bank_balance, P.violet),
            ]
          )}

          {chartCard(
            "Debt/Equity Ratio",
            <Chart type="donut" height={110}
              options={donutOpts(["Debt", "Equity"], [P.red, P.teal])}
              series={[absN(cardData?.current_liabilities), absN(equityVal)]} />,
            [
              valLi("Debt (Liabilities)", cardData?.current_liabilities, P.red),
              valLi("Equity (Net Worth)", equityVal, P.teal),
              txtLi("Ratio", balRatio(cardData?.current_liabilities, equityVal)),
            ]
          )}

          {/* Bar cards */}
          {chartCard(
            "Profit Ratio",
            <Chart type="bar" height={130}
              options={barOpts(["Gross", "Net"], [P.indigo, P.green])}
              series={[{ data: [absN(cardData?.gross_profit), absN(cardData?.net_profit_or_loss)] }]} />,
            [
              plLi("Gross Profit", cardData?.gross_profit, P.indigo),
              plLi("Net Profit", cardData?.net_profit_or_loss, P.green),
              txtLi(
                Number(cardData?.gross_profit) < 0 ? "Gross Loss Ratio" : "Gross Profit Ratio",
                pctRatio(cardData?.gross_profit, cardData?.total_sales)
              ),
            ]
          )}

          {chartCard(
            "Fund Flow",
            <Chart type="bar" height={130}
              options={barOpts(["In", "Out", "Net"], [P.green, P.red, P.indigo])}
              series={[{ data: [absN(cardData?.in_flow), absN(cardData?.out_flow), absN(cardData?.net_flow)] }]} />,
            [
              valLi("Net Flow", cardData?.net_flow, P.indigo),
              valLi("In Flow", cardData?.in_flow, P.green),
              valLi("Out Flow", cardData?.out_flow, P.red),
            ]
          )}

          {chartCard(
            "Inventory Analysis",
            <Chart type="bar" height={130}
              options={barOpts(["Open", "In", "Out", "Close"], [P.sky, P.green, P.amber, P.violet])}
              series={[{ data: [absN(cardData?.opening_stock), absN(cardData?.total_inward), absN(cardData?.total_outward), absN(cardData?.closing_stock)] }]} />,
            [
              valLi("Opening Stock", cardData?.opening_stock, P.sky),
              valLi("Inwards", cardData?.total_inward, P.green),
              valLi("Outwards", cardData?.total_outward, P.amber),
              valLi("Closing Stock", cardData?.closing_stock, P.violet),
            ]
          )}

          {chartCard(
            "Net Profit Ratio",
            <Chart type="bar" height={130}
              options={barOpts(["Net Profit", "Sales"], [P.violet, P.cyan])}
              series={[{ data: [absN(cardData?.net_profit_or_loss), absN(cardData?.total_sales)] }]} />,
            [
              plLi("Net Profit", cardData?.net_profit_or_loss, P.violet),
              valLi("Sales", cardData?.total_sales, P.cyan),
              txtLi(
                Number(cardData?.net_profit_or_loss) < 0 ? "Net Loss Ratio" : "Net Profit Ratio",
                pctRatio(cardData?.net_profit_or_loss, cardData?.total_sales)
              ),
            ]
          )}
        </Row>
      </div>

      {/* Trend charts */}
      <Row className="g-3">
        <Col sm={6}>
          <div className="finance-chart">
            <Card>
              <div className="finance-dash-head">
                Sales Trend{" "}
                <span className="finance-dash-head-span">
                  (
                  {moment(loggedUser?.finance_year?.start_date).format("D-MMM-YYYY")}{" "}
                  to{" "}
                  {moment(loggedUser?.finance_year?.end_date).format("D-MMM-YYYY")}
                  )
                </span>
              </div>
              <Chart options={Options} series={Options?.Series} type="area" width="100%" height={300} />
            </Card>
          </div>
        </Col>
        <Col sm={6}>
          <div className="finance-chart">
            <Card className="px-2">
              <div className="finance-dash-head">Purchase Trend</div>
              <Chart options={Options1} series={Options1?.Series} type="area" width="100%" height={300} />
            </Card>
          </div>
        </Col>
      </Row>

      {/* Income statement & monthly sales */}
      <Row className="g-3 mt-1">
        <Col sm={6}>
          <div className="dk-card h-100">
            <div className="finance-dash-head mb-2">Income Statement</div>
            <Chart options={incomeStmt.options} series={incomeStmt.series} type="bar" height={280} />
          </div>
        </Col>
        <Col sm={6}>
          <div className="dk-card h-100">
            <div className="finance-dash-head mb-2">Monthly Sales</div>
            <Chart options={monthlySales.options} series={monthlySales.series} type="bar" height={280} />
          </div>
        </Col>
      </Row>

      {/* Employee cost & working-capital days */}
      <Row className="g-3 mt-1">
        <Col xxl={4}>
          <div className="dk-card h-100">
            <div className="dk-card-head"><span>EMPLOYEE COST vs SALES</span></div>
            <Chart options={empCostVsSales.options} series={empCostVsSales.series} type="line" height={250} />
          </div>
        </Col>
        <Col md={6} xxl={4}>
          <div className="dk-card h-100">
            <div className="dk-card-head"><span>DAYS RECEIVABLE OUTSTANDING</span></div>
            <Chart options={gauge(P.amber)} series={[42]} type="radialBar" height={190} />
            <div className="dk-gauge-center"><span>days · target ≤ 45</span></div>
            <div className="dk-legend mt-2"><span>Receivable aging</span></div>
            <div className="d-flex gap-2 mt-1">
              {[["0–30", 62, P.green], ["31–60", 24, P.amber], ["61–90", 10, P.sky], ["90+", 4, P.red]].map(a => (
                <div key={a[0]} style={{ flex: 1, textAlign: "center" }}>
                  <div className="dk-progress" style={{ height: 42, display: "flex", alignItems: "flex-end" }}>
                    <span style={{ width: "100%", height: `${a[1]}%`, background: a[2], borderRadius: 4, display: "block" }} />
                  </div>
                  <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 4 }}>{a[0]}<br />{a[1]}%</div>
                </div>
              ))}
            </div>
          </div>
        </Col>
        <Col md={6} xxl={4}>
          <div className="dk-card h-100">
            <div className="dk-card-head"><span>DAYS INVENTORY OUTSTANDING</span></div>
            <Chart options={gauge(P.violet)} series={[36]} type="radialBar" height={190} />
            <div className="dk-gauge-center"><span>days · target ≤ 40</span></div>
            <div className="dk-legend mt-2"><span>Inventory trend</span></div>
            <Chart
              type="area" height={70}
              series={[{ data: [44, 42, 45, 41, 43, 39, 40, 37, 38, 36] }]}
              options={{ chart: { sparkline: { enabled: true } }, stroke: { curve: "smooth", width: 2.5 }, colors: [P.violet], fill: { type: "gradient", gradient: { opacityFrom: 0.3, opacityTo: 0.02 } }, tooltip: { enabled: false } }}
            />
          </div>
        </Col>
      </Row>
      </Container>
    </div>
  )
}

export default FinanceDahboard
